import React, { useEffect, useState } from "react";
import { Zap, Search, ChevronLeft, ChevronRight, Activity, Battery, Plug, Hash, ShieldCheck, ShieldAlert, Layers, ChevronUp, ChevronDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";


interface PoePortData {
    port: number;
    group: number;
    priority: string;
    power_class: string;
    max_power_class: string;
    detection_status: string;
    is_delivering_power: boolean;
    device_detected?: boolean;
    power_available_watts?: number;
    power_consumption_watts?: number;
}


interface PoeSummary {
    total_power_watts: number;
    consumed_power_watts: number;
    available_power_watts: number;
    ports_delivering_power: number;
}


interface PoeInfoProps {
    client_id: string;
    hideHeader?: boolean;
    defaultItemsPerPage?: number;
    viewType?: "table" | "card";
    onDataLoaded?: (hasData: boolean) => void;
}


const PoeInfo: React.FC<PoeInfoProps> = ({
    client_id,
    hideHeader = false,
    defaultItemsPerPage = 5,
    viewType = "table",
    onDataLoaded
}) => {
    const [rawPorts, setRawPorts] = useState<PoePortData[]>([]);
    const [summary, setSummary] = useState<PoeSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");


    // Local Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(defaultItemsPerPage);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);


    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;


    useEffect(() => {
        if (asset_id) fetchData();
    }, [asset_id]);


    const fetchData = async () => {
        setLoading(true);
        try {
            const params: any = {
                query: JSON.stringify({ asset_id: asset_id, client_id: client_id }),
                sort_by: "created",
                sort_order: "desc",
                limit: 1
            };


            const response = await baseapi.get("/poe_info", { params });
            if (response.data.status && response.data.data?.length > 0) {
                const latest = response.data.data[0];
                setSummary({
                    total_power_watts: latest.total_power_watts || 0,
                    consumed_power_watts: latest.consumed_power_watts || 0,
                    available_power_watts: latest.available_power_watts || 0,
                    ports_delivering_power: latest.ports_delivering_power || 0
                });


                try {
                    const parsed = typeof latest.poe_data === 'string'
                        ? JSON.parse(latest.poe_data)
                        : (latest.poe_data || []);
                    setRawPorts(parsed);
                } catch (e) {
                    console.error("Error parsing poe_data", e);
                    setRawPorts([]);
                }
                if (onDataLoaded) onDataLoaded(true);
            } else {
                setRawPorts([]);
                setSummary(null);
                if (onDataLoaded) onDataLoaded(false);
            }
        } catch (error: any) {
            showError(error.message);
            if (onDataLoaded) onDataLoaded(false);
        } finally {
            setLoading(false);
        }
    };



    const filteredPorts = rawPorts.filter(p =>
        p.port.toString().includes(searchTerm) ||
        p.detection_status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.power_class?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }
        setSortConfig({ key, direction: newDirection });
        setCurrentPage(1);
    };

    const sortedPorts = [...filteredPorts].sort((a, b) => {
        if (!sortConfig) return 0;
        const { key, direction } = sortConfig;

        let aValue = a[key as keyof PoePortData];
        let bValue = b[key as keyof PoePortData];

        if (aValue === undefined || aValue === null) aValue = '';
        if (bValue === undefined || bValue === null) bValue = '';

        if (key === 'port' || key === 'power_consumption_watts' || key === 'power_available_watts') {
            return direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
        }

        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();

        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPages = Math.ceil(sortedPorts.length / itemsPerPage);
    const pagedData = sortedPorts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const SortableHeader = ({ label, sortKey, className = "" }: { label: string; sortKey: string; className?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`px-4 py-2 text-[9px] font-black text-slate-700 dark:text-slate-200 uppercase tracking-widest cursor-pointer group select-none ${className}`}
            >
                <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`}>
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={10} className="text-orange-500" />
                            ) : (
                                <ChevronDown size={10} className="text-orange-500" />
                            )
                        ) : (
                            <ChevronDown size={10} className="text-slate-400 dark:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                    </div>
                </div>
            </th>
        );
    };


    if (loading) {
        return (
            <div className="space-y-4 animate-fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-20 bg-slate-200/70 dark:bg-slate-800/50 rounded-xl animate-pulse border border-slate-300/60 dark:border-slate-700/50"></div>
                    ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-2">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-28 bg-slate-200/70 dark:bg-slate-800/50 rounded-lg animate-pulse border border-slate-300/60 dark:border-slate-700/50"></div>
                    ))}
                </div>
            </div>
        );
    }


    return (
        <div className="space-y-4 animate-fade-in">
            {!hideHeader && (
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center">
                        <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                            <Zap className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">PoE Management</h2>
                    </div>
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-200 w-3.5 h-3.5" />
                        <input
                            type="text"
                            placeholder="Filter port..."
                            className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg pl-9 pr-4 py-1.5 text-[13px] text-gray-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-400 focus:outline-none focus:border-orange-500 transition-all font-medium"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>
            )}


            {/* PoE Metric Summary Cards */}
            {summary && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 rounded-lg shadow-sm hover:border-orange-500/20 transition-colors relative overflow-hidden group">
                        <div className="flex items-center gap-2 mb-1">
                            <Battery className="w-3 h-3 text-orange-500" />
                            <span className="text-[9px] font-black text-slate-500 dark:text-slate-200 uppercase tracking-widest">Total Power Budget</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                            <span className="text-xl font-black text-gray-900 dark:text-white">{summary.total_power_watts}</span>
                            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-200 uppercase">W</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 rounded-lg shadow-sm hover:border-orange-500/20 transition-colors relative overflow-hidden group">
                        <div className="flex items-center gap-2 mb-1">
                            <Zap className="w-3 h-3 text-emerald-500" />
                            <span className="text-[9px] font-black text-slate-500 dark:text-slate-200 uppercase tracking-widest">Consumption</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                            <span className="text-xl font-black text-emerald-600 dark:text-emerald-500">{summary.consumed_power_watts}</span>
                            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-200 uppercase">W</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 rounded-lg shadow-sm hover:border-orange-500/20 transition-colors relative overflow-hidden group">
                        <div className="flex items-center gap-2 mb-1">
                            <ShieldCheck className="w-3 h-3 text-sky-500" />
                            <span className="text-[9px] font-black text-slate-500 dark:text-slate-200 uppercase tracking-widest">Available</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                            <span className="text-xl font-black text-sky-600 dark:text-sky-500">{summary.available_power_watts}</span>
                            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-200 uppercase">W</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 rounded-lg shadow-sm hover:border-orange-500/20 transition-colors relative overflow-hidden group">
                        <div className="flex items-center gap-2 mb-1">
                            <Activity className="w-3 h-3 text-slate-500 dark:text-white/50" />
                            <span className="text-[9px] font-black text-slate-500 dark:text-slate-200 uppercase tracking-widest">Active Ports</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                            <span className="text-xl font-black text-gray-900 dark:text-white">{summary.ports_delivering_power}</span>
                            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-200 uppercase">Ports</span>
                        </div>
                    </div>
                </div>
            )}


            {/* Per-Port PoE Details - Bordered Section */}
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm">
                {/* Section Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-700/40">
                    <div className="flex items-center gap-2">
                        <Plug className="w-3.5 h-3.5 text-orange-500" />
                        <span className="text-[11px] font-black text-gray-900 dark:text-white uppercase tracking-widest">Per-Port PoE Details</span>
                    </div>
                    <span className="text-[10px] text-slate-500 dark:text-slate-200 font-bold">{filteredPorts.length} Ports</span>
                </div>


                <div className="">
                    {pagedData.length === 0 ? (
                        <div className="bg-slate-50 dark:bg-slate-900/30 rounded-lg border border-slate-200 dark:border-slate-800 border-dashed py-8 text-center m-4">
                            <Zap className="w-6 h-6 text-slate-400 dark:text-slate-700 mx-auto mb-2" />
                            <p className="text-slate-500 dark:text-slate-200 text-[11px] font-bold uppercase tracking-widest">No PoE data detected</p>
                        </div>
                    ) : viewType === "card" ? (
                        <div className="space-y-3 p-4">
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-2">
                                {pagedData.map((item) => (
                                    <div key={item.port} className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-2.5 hover:border-orange-500/40 transition-all shadow-sm dark:shadow-md overflow-hidden group relative">
                                        <Plug className="absolute -right-2 -bottom-2 w-12 h-12 text-slate-200/50 dark:text-slate-200/5 group-hover:text-slate-300 dark:group-hover:text-slate-200/10 transition-colors" />


                                        <div className="flex justify-between items-start mb-1.5 relative z-10">
                                            <div className="flex items-center gap-1.5">
                                                <div className="bg-white dark:bg-slate-800 p-1 rounded border border-slate-200 dark:border-slate-700">
                                                    <Hash size={10} className="text-orange-500" />
                                                </div>
                                                <span className="text-sm font-black text-gray-900 dark:text-white leading-none">P{item.port}</span>
                                            </div>
                                            <div className={`flex items-center gap-1 px-1 py-0.5 rounded-[4px] text-[8px] font-black uppercase border ${item.is_delivering_power
                                                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/20'
                                                : 'bg-slate-200 dark:bg-slate-800 text-orange-600 dark:text-orange-500 border-slate-300 dark:border-slate-700'}`}>
                                                {item.is_delivering_power ? <ShieldCheck size={8} /> : <ShieldAlert size={8} />}
                                                {item.detection_status || 'Offline'}
                                            </div>
                                        </div>


                                        <div className="grid grid-cols-3 gap-1 mt-3 relative z-10">
                                            <div className="bg-white dark:bg-slate-950/50 p-1.5 rounded-md border border-slate-200 dark:border-slate-800/50 flex flex-col items-center justify-between min-h-[42px]">
                                                <div className="flex flex-col items-center">
                                                    <Zap size={10} className="text-emerald-500 mb-0.5" />
                                                    <span className="text-[6px] font-black text-slate-500 dark:text-slate-200 uppercase block tracking-tighter">Usage</span>
                                                </div>
                                                <span className={`text-[9px] font-black ${item.is_delivering_power ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-600'}`}>
                                                    {(item.power_consumption_watts || 0).toFixed(1)}W
                                                </span>
                                            </div>
                                            <div className="bg-white dark:bg-slate-950/50 p-1.5 rounded-md border border-slate-200 dark:border-slate-800/50 flex flex-col items-center justify-between min-h-[42px]">
                                                <div className="flex flex-col items-center">
                                                    <Battery size={10} className="text-orange-500 mb-0.5" />
                                                    <span className="text-[6px] font-black text-slate-500 dark:text-slate-200 uppercase block tracking-tighter">Limit</span>
                                                </div>
                                                <span className="text-[9px] font-black text-gray-900 dark:text-white">{item.max_power_class || '0W'}</span>
                                            </div>
                                            <div className="bg-white dark:bg-slate-950/50 p-1.5 rounded-md border border-slate-200 dark:border-slate-800/50 flex flex-col items-center justify-between min-h-[42px]">
                                                <div className="flex flex-col items-center">
                                                    <Layers size={10} className="text-sky-500 mb-0.5" />
                                                    <span className="text-[6px] font-black text-slate-500 dark:text-slate-200 uppercase block tracking-tighter">Class</span>
                                                </div>
                                                <span className="text-[9px] font-black text-gray-900 dark:text-white">{item.power_class || "-"}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>


                            {/* Local Pagination */}
                            {totalPages > 1 && (
                                <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-900/50 p-2 px-4 rounded-xl border border-slate-200 dark:border-slate-800">
                                    <span className="text-[10px] text-slate-500 dark:text-slate-200 font-black uppercase tracking-widest">
                                        Page <span className="text-gray-900 dark:text-white">{currentPage}</span> of {totalPages}
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="p-1 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-20 transition-all font-bold cursor-pointer"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="p-1 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-20 transition-all font-bold cursor-pointer"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 overflow-hidden shadow-sm dark:shadow-2xl">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50 dark:bg-gray-800/50 border-b border-slate-200 dark:border-gray-700">
                                                <SortableHeader label="Port" sortKey="port" />
                                                <SortableHeader label="Status" sortKey="detection_status" />
                                                <SortableHeader label="Class" sortKey="power_class" />
                                                <SortableHeader label="Priority" sortKey="priority" />
                                                <SortableHeader label="Consumption" sortKey="power_consumption_watts" className="text-right" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200 dark:divide-gray-700">
                                            {pagedData.map((item) => (
                                                <tr key={item.port} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors group">
                                                    <td className="px-4 py-2">
                                                        <span className="text-gray-900 dark:text-white text-[11px] font-black font-mono">#{item.port}</span>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-black border uppercase ${item.is_delivering_power
                                                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/20'
                                                            : 'bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-200 border-slate-200 dark:border-slate-500/20'}`}>
                                                            {item.detection_status || 'Unknown'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2 text-slate-700 dark:text-slate-200 text-[10px] font-bold">{item.power_class || '-'}</td>
                                                    <td className="px-4 py-2 text-slate-700 dark:text-slate-200 text-[9px] font-bold">{item.priority || 'Low'}</td>
                                                    <td className="px-4 py-2 text-right">
                                                        <span className={`text-[10px] font-black ${item.is_delivering_power ? 'text-emerald-600 dark:text-emerald-500' : 'text-slate-400 dark:text-slate-700'}`}>
                                                            {(item.power_consumption_watts || 0).toFixed(2)} W
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Local Pagination for Table */}
                            {totalPages > 1 && (
                                <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-900/50 p-2 px-4 rounded-xl border border-slate-200 dark:border-slate-800 m-4">
                                    <span className="text-[10px] text-slate-500 dark:text-slate-200 font-black uppercase tracking-widest">
                                        Page <span className="text-gray-900 dark:text-white">{currentPage}</span> of {totalPages}
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="p-1 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-20 transition-all font-bold cursor-pointer"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="p-1 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-20 transition-all font-bold cursor-pointer"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};



export default PoeInfo;
